/**
 * touchTuning.ts — pure input-shaping math for the touch controls. No DOM and no
 * three.js, so it's unit-tested in tests/input/. Keyboard and gamepad never import
 * this module, which is what STRUCTURALLY guarantees desktop steering is unaffected
 * by any touch tuning.
 */
import { clamp } from '@/math/angles';

/**
 * Shape a raw normalized stick axis (-1..1) into a steer/throttle value:
 *   • deadzone    — ignore tiny travel around centre (kills touch jitter; the ball
 *                   no longer twitches off the nose from a shaky thumb).
 *   • sensitivity — endpoint-preserving gain. >1 gives MORE steer for the same
 *                   partial throw (a full turn with less thumb travel → less reach
 *                   and strain); <1 is calmer. Both centre (0) and full lock (±1)
 *                   are ALWAYS preserved, so top-end agility never changes.
 *   • expo        — blends toward a cubic curve to soften SMALL inputs for fine
 *                   ball control near centre (endpoints stay fixed).
 *
 * Always returns within [-1, 1], sign-preserving and odd-symmetric.
 */
export function shapeAxis(v: number, deadzone: number, expo: number, sensitivity: number): number {
  const sign = Math.sign(v);
  const m = Math.abs(v);
  if (m <= deadzone) return 0;
  // Rescale the live range past the deadzone back to 0..1 (so full throw = full lock).
  const t = (m - deadzone) / (1 - deadzone);
  // Sensitivity as a power gain that fixes both endpoints (t=0→0, t=1→1).
  const sens = clamp(sensitivity, 0.25, 3);
  const gained = Math.pow(t, 1 / sens);
  // Expo softens the low end; the cubic blend also fixes both endpoints.
  const e = clamp(expo, 0, 1);
  const curved = (1 - e) * gained + e * gained * gained * gained;
  return sign * curved;
}

/**
 * M92 — joystick ORIGIN geometry (the reverse-stall fixes). Pure, so the three
 * behaviours that made "pull down = reverse" unreliable are each unit-tested:
 *
 *  1. clampStickOrigin — when the stick re-centres under a fresh touch, keep the
 *     origin far enough inside the screen that a FULL throw in every direction is
 *     physically reachable. Before this, a tap near the bottom edge parked the
 *     origin AT the finger: there was no room left below it, so full reverse was
 *     literally unreachable (and the tap itself read as neutral).
 *  2. followStickOrigin — the rim-follow "leash": when the finger travels beyond
 *     the stick's max radius, the origin is dragged along so the knob rides the
 *     rim. A fast swipe down therefore ALWAYS ends at full-down, no matter where
 *     the origin started — reversing out of full-forward no longer needs a
 *     2×-radius thumb journey, and micro-corrections stay relative to where the
 *     thumb actually is.
 */

/** Clamp a re-centred stick origin so a full throw stays on-screen. `reach` =
 *  max knob radius + a small margin. Degenerate viewports (smaller than 2×reach)
 *  fall back to the viewport centre on that axis. */
export function clampStickOrigin(
  x: number,
  y: number,
  viewportW: number,
  viewportH: number,
  reach: number,
): { x: number; y: number } {
  return {
    x: viewportW < reach * 2 ? viewportW / 2 : clamp(x, reach, viewportW - reach),
    y: viewportH < reach * 2 ? viewportH / 2 : clamp(y, reach, viewportH - reach),
  };
}

/** Rim-follow leash: returns the origin unchanged while the finger is within
 *  `maxRadius`; beyond it, the origin moves the overshoot toward the finger so
 *  the knob sits exactly on the rim in the finger's direction. */
export function followStickOrigin(
  originX: number,
  originY: number,
  px: number,
  py: number,
  maxRadius: number,
): { x: number; y: number } {
  const dx = px - originX;
  const dy = py - originY;
  const dist = Math.hypot(dx, dy);
  if (dist <= maxRadius) return { x: originX, y: originY };
  const k = (dist - maxRadius) / dist;
  return { x: originX + dx * k, y: originY + dy * k };
}

/**
 * M115 — the resolved set of numbers the joystick actually drives, so a whole
 * FEEL PROFILE can be swapped wholesale (grown-up vs Big Kid) without forking a
 * single line of input code. Both profiles live in tuning.touch.
 */
export interface TouchProfile {
  steerDeadzone: number;
  steerExpo: number;
  steerSensitivity: number;
  /** 1/s low-pass rate on the reported steer; 0 = filter OFF (adult default). */
  steerSmoothRate: number;
  throttleDeadzone: number;
  throttleSensitivity: number;
  reverseSensitivity: number;
  /** The visible ring's DIAMETER in px (the --joy-base CSS var). */
  baseRadiusPx: number;
  /** The knob's throw RADIUS in px — full lock at this much finger travel. */
  maxRadiusPx: number;
  restRaisePx: number;
  restInsetPx: number;
}

/**
 * M115 — shrink an oversized pad so it fits the live viewport.
 *
 * The Big Kid pad is sized for an iPad; the very same numbers would swallow a
 * 375 px-tall landscape phone and put full-forward off the top of the screen.
 * So the whole profile scales down UNIFORMLY (ring, throw and resting offsets
 * together, keeping its proportions) until the resting ring fits inside
 * `widthFrac` × `heightFrac` of the viewport, and the resting centre is then
 * clamped so a FULL throw stays reachable above, below and inboard of it.
 *
 * Pure, so every one of those guarantees is unit-tested. Only the Big Kid
 * profile is ever passed through here — the adult profile is used verbatim, so
 * grown-up feel is byte-identical by construction.
 */
export function fitTouchProfile(
  p: TouchProfile,
  viewportW: number,
  viewportH: number,
  widthFrac: number,
  heightFrac: number,
  minScale: number,
  edgeMargin = 12,
): TouchProfile {
  const capW = Math.max(0, viewportW) * widthFrac;
  const capH = Math.max(0, viewportH) * heightFrac;
  const scale = clamp(Math.min(capW, capH) / Math.max(1, p.baseRadiusPx), minScale, 1);
  const maxRadiusPx = p.maxRadiusPx * scale;
  // A full throw must be reachable in every direction from the RESTING centre
  // (clampStickOrigin only guards origins that re-centre under a fresh touch).
  const reach = maxRadiusPx + edgeMargin;
  const raiseHi = Math.max(reach, viewportH - reach);
  const insetHi = Math.max(reach, viewportW - reach);
  return {
    ...p,
    baseRadiusPx: p.baseRadiusPx * scale,
    maxRadiusPx,
    restRaisePx: clamp(p.restRaisePx * scale, reach, raiseHi),
    restInsetPx: clamp(p.restInsetPx * scale, reach, insetHi),
  };
}

/**
 * M115 — one-pole low-pass step used ONLY on the Big Kid steer axis.
 *
 * `rate` is in 1/s (the same convention as tuning.car.steerSmooth): the value
 * closes ~63% of the remaining gap every 1/rate seconds, and settles EXACTLY on
 * the target, so full lock stays reachable — it just takes a beat.
 *
 * `rate <= 0` returns the target untouched, which is how the grown-up profile
 * is a pure pass-through rather than a filter that happens to be tuned fast.
 * A non-positive dt holds the current value (several sim steps can share one
 * wall-clock instant when the fixed-step loop catches up).
 */
export function smoothTowards(current: number, target: number, rate: number, dtS: number): number {
  if (rate <= 0) return target;
  if (!(dtS > 0)) return current;
  return current + (target - current) * (1 - Math.exp(-rate * dtS));
}
